import type {
  HomeworkAssignmentSnapshot,
  SubmissionSnapshot,
  SubmissionStatus,
} from '@wx-weiqi/contracts';

export function canReviewHomeworkSubmission(
  submission: SubmissionSnapshot | null | undefined,
): boolean {
  return (
    submission != null &&
    (submission.status === 'NEEDS_REVIEW' ||
      (submission.reviewedByMembershipId !== null &&
        (submission.status === 'CORRECT' || submission.status === 'INCORRECT')))
  );
}

export type HomeworkProblemGradingStatus = 'CORRECT' | 'INCORRECT' | 'PENDING' | 'UNANSWERED';

export interface HomeworkProblemGradingRow {
  readonly assignment: HomeworkAssignmentSnapshot;
  readonly submission: SubmissionSnapshot | null;
  readonly status: HomeworkProblemGradingStatus;
  readonly elapsedMs: number | null;
}

export interface HomeworkProblemGradingSummary {
  readonly problemId: string;
  readonly rows: readonly HomeworkProblemGradingRow[];
  readonly correct: number;
  readonly incorrect: number;
  readonly pending: number;
  readonly unanswered: number;
  readonly timedSampleCount: number;
  readonly averageElapsedMs: number | null;
}

export interface HomeworkGradingProgress {
  /** Number of students whose latest answer for every problem has a final grade. */
  readonly graded: number;
  /** Number of students assigned this homework. */
  readonly total: number;
  /** Number of student/problem pairs with a final grade. */
  readonly gradedAnswers: number;
  /** Total number of expected student/problem answers. */
  readonly totalAnswers: number;
  readonly fullyGraded: boolean;
  readonly label: '已批改' | '未批改';
}

export interface HomeworkAssignmentPerformance {
  readonly assignment: HomeworkAssignmentSnapshot;
  readonly correct: number;
  readonly incorrect: number;
  readonly pending: number;
  readonly unanswered: number;
  readonly total: number;
  readonly accuracy: number;
  readonly totalElapsedMs: number;
}

export interface StudentHomeworkResultRow {
  readonly questionNumber: number;
  readonly problemId: string;
  readonly title: string;
  readonly status: HomeworkProblemGradingStatus;
  readonly attempt: number | null;
  readonly elapsedMs: number | null;
}

/** Current-problem submissions first, oldest latest-attempt timestamp first; stable across refreshes. */
export function sortHomeworkGradingRows(
  rows: readonly HomeworkProblemGradingRow[],
): readonly HomeworkProblemGradingRow[] {
  return Object.freeze(
    [...rows].sort((left, right) => {
      if (left.submission === null && right.submission !== null) return 1;
      if (left.submission !== null && right.submission === null) return -1;
      if (left.submission !== null && right.submission !== null) {
        const difference =
          Date.parse(left.submission.submittedAt) - Date.parse(right.submission.submittedAt);
        if (difference !== 0) return difference;
      }
      return (
        left.assignment.studentMembershipId.localeCompare(right.assignment.studentMembershipId) ||
        left.assignment.assignmentId.localeCompare(right.assignment.assignmentId)
      );
    }),
  );
}

function isFinalGrade(status: SubmissionStatus): boolean {
  return status === 'CORRECT' || status === 'INCORRECT';
}

function presentationStatus(submission: SubmissionSnapshot | null): HomeworkProblemGradingStatus {
  if (submission === null) return 'UNANSWERED';
  if (submission.status === 'CORRECT' || submission.status === 'INCORRECT') {
    return submission.status;
  }
  return 'PENDING';
}

/**
 * Returns the highest attempt for one problem without relying on response order.
 * In the impossible event of duplicate attempt numbers, the later submitted row
 * (and finally the lexicographically larger id) wins deterministically.
 */
export function latestHomeworkSubmissionForProblem(
  assignment: HomeworkAssignmentSnapshot,
  problemId: string,
): SubmissionSnapshot | null {
  let latest: SubmissionSnapshot | null = null;
  for (const submission of assignment.submissions) {
    if (submission.problemId !== problemId) continue;
    if (
      latest === null ||
      submission.attempt > latest.attempt ||
      (submission.attempt === latest.attempt && submission.submittedAt > latest.submittedAt) ||
      (submission.attempt === latest.attempt &&
        submission.submittedAt === latest.submittedAt &&
        submission.submissionId > latest.submissionId)
    ) {
      latest = submission;
    }
  }
  return latest;
}

export function studentHomeworkResultRows(
  assignment: HomeworkAssignmentSnapshot,
): readonly StudentHomeworkResultRow[] {
  return Object.freeze(
    [...assignment.items]
      .sort((left, right) => left.sortOrder - right.sortOrder)
      .map((item, index) => {
        const submission = latestHomeworkSubmissionForProblem(assignment, item.problem.problemId);
        return Object.freeze({
          questionNumber: index + 1,
          problemId: item.problem.problemId,
          title: item.problem.title,
          status: presentationStatus(submission),
          attempt: submission?.attempt ?? null,
          elapsedMs: submission?.elapsedMs ?? null,
        });
      }),
  );
}

export function summarizeHomeworkProblem(
  assignments: readonly HomeworkAssignmentSnapshot[],
  problemId: string,
): HomeworkProblemGradingSummary {
  const rows = assignments.map((assignment): HomeworkProblemGradingRow => {
    const submission = latestHomeworkSubmissionForProblem(assignment, problemId);
    return Object.freeze({
      assignment,
      submission,
      status: presentationStatus(submission),
      elapsedMs: submission?.elapsedMs ?? null,
    });
  });
  const timedSamples = rows.flatMap((row) => (row.elapsedMs === null ? [] : [row.elapsedMs]));
  const averageElapsedMs =
    timedSamples.length === 0
      ? null
      : timedSamples.reduce((total, elapsedMs) => total + elapsedMs, 0) / timedSamples.length;

  return Object.freeze({
    problemId,
    rows: Object.freeze(rows),
    correct: rows.filter((row) => row.status === 'CORRECT').length,
    incorrect: rows.filter((row) => row.status === 'INCORRECT').length,
    pending: rows.filter((row) => row.status === 'PENDING').length,
    unanswered: rows.filter((row) => row.status === 'UNANSWERED').length,
    timedSampleCount: timedSamples.length,
    averageElapsedMs,
  });
}

export function summarizeHomeworkGrading(
  assignments: readonly HomeworkAssignmentSnapshot[],
): HomeworkGradingProgress {
  let graded = 0;
  let gradedAnswers = 0;
  let totalAnswers = 0;

  for (const assignment of assignments) {
    let assignmentGraded = true;
    for (const item of assignment.items) {
      totalAnswers += 1;
      const latest = latestHomeworkSubmissionForProblem(assignment, item.problem.problemId);
      if (latest !== null && isFinalGrade(latest.status)) {
        gradedAnswers += 1;
      } else {
        assignmentGraded = false;
      }
    }
    if (assignment.items.length > 0 && assignmentGraded) graded += 1;
  }

  const total = assignments.length;
  const fullyGraded = total > 0 && graded === total;
  return Object.freeze({
    graded,
    total,
    gradedAnswers,
    totalAnswers,
    fullyGraded,
    label: fullyGraded ? '已批改' : '未批改',
  });
}

export function summarizeHomeworkAssignmentPerformance(
  assignment: HomeworkAssignmentSnapshot,
): HomeworkAssignmentPerformance {
  let correct = 0;
  let incorrect = 0;
  let pending = 0;
  let unanswered = 0;
  let totalElapsedMs = 0;

  for (const item of assignment.items) {
    const submission = latestHomeworkSubmissionForProblem(assignment, item.problem.problemId);
    if (submission?.elapsedMs !== null && submission?.elapsedMs !== undefined) {
      totalElapsedMs += submission.elapsedMs;
    }
    if (submission === null) unanswered += 1;
    else if (submission.status === 'CORRECT') correct += 1;
    else if (submission.status === 'INCORRECT') incorrect += 1;
    else pending += 1;
  }

  const total = assignment.items.length;
  return Object.freeze({
    assignment,
    correct,
    incorrect,
    pending,
    unanswered,
    total,
    accuracy: total === 0 ? 0 : (correct / total) * 100,
    totalElapsedMs,
  });
}

export function rankHomeworkAssignmentPerformances(
  assignments: readonly HomeworkAssignmentSnapshot[],
): readonly HomeworkAssignmentPerformance[] {
  return Object.freeze(
    assignments
      .map(summarizeHomeworkAssignmentPerformance)
      .sort(
        (left, right) =>
          right.accuracy - left.accuracy ||
          right.correct - left.correct ||
          left.totalElapsedMs - right.totalElapsedMs ||
          left.assignment.studentMembershipId.localeCompare(right.assignment.studentMembershipId),
      ),
  );
}
